import { useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Center, Spinner, VStack, Text } from '@chakra-ui/react';

/**
 * OAuth/SSO callback page.
 *
 * The backend redirects here after SAML/OIDC authentication with a JWT token
 * delivered as either a query param (?jwt=<token>) or a path param
 * (/oauth-success/<token>).
 *
 * This page:
 *   1. Stores the JWT in localStorage for parent-window polling
 *   2. Sends the JWT to the opener via postMessage (if popup)
 *   3. Falls back to full-page redirect with ?jwt= (if no opener)
 */
export function OAuthSuccess() {
  const [searchParams] = useSearchParams();
  const params = useParams<{ jwt?: string }>();

  useEffect(() => {
    // JWT can arrive as ?jwt= query param or as a path segment
    const jwt = searchParams.get('jwt') || params.jwt;

    if (!jwt) {
      console.error('OAuthSuccess: No JWT found in URL');
      return;
    }

    // Store in localStorage for parent-window polling (COOP fallback)
    try {
      localStorage.setItem('sso_pending_jwt', jwt);
      localStorage.setItem('sso_jwt_timestamp', Date.now().toString());
    } catch (e) {
      console.error('OAuthSuccess: Error storing in localStorage:', e);
    }

    // Check if opener is from our domain
    let isOpenerFromOurDomain = false;
    let shouldRedirect = false;

    if (window.opener && !window.opener.closed) {
      try {
        const openerOrigin = window.opener.location.origin;
        isOpenerFromOurDomain = openerOrigin === window.location.origin;
      } catch {
        // Cross-origin — can't access opener
        shouldRedirect = true;
      }
    }

    // Send JWT to opener via postMessage
    if (
      window.opener &&
      !window.opener.closed &&
      isOpenerFromOurDomain &&
      !shouldRedirect
    ) {
      try {
        window.opener.postMessage(
          { type: 'oauth-success', token: jwt, jwt: jwt },
          '*',
        );
        setTimeout(() => window.close(), 1000);
      } catch {
        shouldRedirect = true;
      }
    }

    // No valid opener — redirect to app root with JWT
    if (
      !window.opener ||
      window.opener.closed ||
      !isOpenerFromOurDomain ||
      shouldRedirect
    ) {
      window.location.href = `${window.location.origin}/login?jwt=${jwt}`;
    }
  }, [searchParams, params.jwt]);

  return (
    <Center minH="100vh" bg="surface.primary">
      <VStack gap={4}>
        <Spinner size="xl" color="button.primary" />
        <Text color="text.secondary">Completing authentication...</Text>
      </VStack>
    </Center>
  );
}
